// Permissions réelles (Phase 0 du plan de migration) — remplace EMP_ROLES[firstname] ===
// "Filialleiter" (comparaison de prénom en dur, aucune sécurité réelle) par le vrai rôle
// du compte connecté (user.role, renvoyé par /api/login) et, quand un contrôle fin par
// permission est nécessaire, par le même GET /api/roles-permissions que
// StoreFlowPermissions.can() utilise côté vanilla (security-engine.js).
import { useState, useEffect } from "react";
import { apiJSON } from "./api.js";

// Même liste que MANAGEMENT_ROLES côté serveur (server.js) — dupliquée volontairement,
// aucune donnée partagée entre client et serveur à ce niveau (même principe que
// LEADER_ROLES/MANAGEMENT_ROLES dupliqués entre plusieurs fichiers côté vanilla).
const MANAGEMENT_ROLES = ["FLA", "FL", "Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"];

// Module Administration "gelé" (2026-08-13, retour utilisateur explicite : "le risque est
// que n'importe qui s'amuse avec ce module") — le cluster admin/users/stores/roles/system
// est retiré de TOUS les rôles configurables (voir la donnée réelle role_permissions,
// corrigée en même temps que ce fichier), sauf pour ce compte précis, qui garde un accès
// individuel même si son rôle (FLA) n'a plus la permission via role_permissions. Jamais un
// bypass total : seulement pour ces 5 préfixes précis, tout le reste de ses permissions
// continue de suivre role_permissions normalement.
const ADMIN_CLUSTER_PREFIXES = ["admin.", "users.", "stores.", "roles.", "system."];
const ADMIN_OVERRIDE_EMPLOYEE_IDS = ["17002"];
function isAdminClusterOverride(user, permissionId) {
  return ADMIN_OVERRIDE_EMPLOYEE_IDS.includes(String(user?.employeeId || user?.proId || "")) &&
    ADMIN_CLUSTER_PREFIXES.some((prefix) => permissionId.startsWith(prefix));
}

export function isManager(user) {
  return !!user && MANAGEMENT_ROLES.includes(user.role);
}

// Retour utilisateur explicite (2026-08-12) : "je ne veux pas avoir à faire un
// rafraîchissement de la page d'abord" — quand un admin coche/décoche une permission,
// TOUS les comptes du rôle concerné, déjà connectés ailleurs, doivent voir l'effet sans
// recharger. Avant, rolesCache était rempli une seule fois puis jamais réinvalidé pour
// toute la durée de vie de la page — un mini pub/sub + poll 15s (même cadence que le
// reste de l'app, voir la boucle de notifications dans App.jsx) le garde vivant. Un seul
// setInterval partagé par tous les usePermission() montés à la fois, pas un par appel.
let rolesCache = null; // { roleName: [permissionIds...] }
let rolesPromise = null;
let pollStarted = false;
const listeners = new Set();

function fetchRolePermissions() {
  return apiJSON("/api/roles-permissions")
    .then((data) => {
      rolesCache = (data && data.success && data.roles && typeof data.roles === "object") ? data.roles : {};
      listeners.forEach((fn) => fn());
      return rolesCache;
    })
    .catch(() => rolesCache || {});
}

function loadRolePermissions() {
  if (rolesCache) return Promise.resolve(rolesCache);
  if (!rolesPromise) rolesPromise = fetchRolePermissions();
  return rolesPromise;
}

function ensurePolling() {
  if (pollStarted) return;
  pollStarted = true;
  setInterval(fetchRolePermissions, 15000);
}

// Appelé juste après une modification réussie (POST /api/roles-permissions) pour que
// l'appareil de l'admin qui vient de cocher/décocher voie l'effet tout de suite, sans
// attendre le prochain tick du poll partagé (voir togglePermission dans App.jsx).
export function refreshRolePermissions() {
  return fetchRolePermissions();
}

// Contrôle fin par identifiant de permission (ex. "article-finder.manage") — à utiliser
// module par module (Phase 1+) au fur et à mesure qu'on connaît l'ID réel qui correspond
// à chaque garde encore basée sur isManager() en attendant.
//
// NEXORA_ADMIN court-circuite TOUJOURS à true, jamais lu depuis role_permissions — même
// philosophie que requireRole()/isSuperAdminServer côté serveur (server.js, voir
// isNexoraAdminRole) : "sans ce court-circuit, il faudrait ajouter NEXORA_ADMIN à chaque
// permission existante une par une". Bug réel trouvé en vérifiant Admin > Filiales
// (2026-08-02) : role_permissions.roles n'a AUCUNE entrée "NEXORA_ADMIN" (seulement les
// rôles configurables) — sans ce court-circuit, canViewUsers etc. étaient silencieusement
// false pour le vrai compte administrateur, rendant tout Admin invisible pour lui.
export function usePermission(user, permissionId) {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!user || !permissionId) { setAllowed(false); return; }
    if (user.role === "NEXORA_ADMIN") { setAllowed(true); return; }
    if (isAdminClusterOverride(user, permissionId)) { setAllowed(true); return; }
    ensurePolling();
    const recompute = () => {
      if (cancelled || !rolesCache) return;
      const list = rolesCache[user.role];
      setAllowed(Array.isArray(list) && list.includes(permissionId));
    };
    loadRolePermissions().then(() => { if (!cancelled) recompute(); });
    listeners.add(recompute);
    return () => { cancelled = true; listeners.delete(recompute); };
  }, [user, permissionId]);
  return allowed;
}
